import React, { useState, useEffect } from 'react';
import { X, Calendar as CalendarIcon, Clock, MapPin, FileText, Plus, DollarSign } from 'lucide-react';
import { collection, addDoc, updateDoc, doc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

const AppointmentModal = ({ isOpen, onClose, editingAppointment = null, selectedDate = null }) => {
  const { currentUser } = useAuth();
  const [clients, setClients] = useState([]);
  const [pets, setPets] = useState([]);
  const [selectedClientPets, setSelectedClientPets] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Quick add states
  const [showQuickAddClient, setShowQuickAddClient] = useState(false);
  const [showQuickAddPet, setShowQuickAddPet] = useState(false);
  const [quickClientData, setQuickClientData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [quickPetData, setQuickPetData] = useState({
    name: '',
    type: 'dog',
    breed: ''
  });

  // Service pricing presets
  const servicePresets = {
    walking: 25,
    sitting: 50,
    daycare: 40,
    checkin: 20,
  };

  const [formData, setFormData] = useState({
    clientId: '',
    petId: '',
    startDate: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    endDate: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    time: '09:00',
    serviceType: 'walking',
    location: '',
    duration: '30',
    notes: '',
    amount: 25,
    paymentStatus: 'pending',
  });

  // Fetch clients and pets
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;

      try {
        // Fetch clients
        const clientsRef = collection(db, 'clients');
        const clientsQuery = query(clientsRef, where('sitterId', '==', currentUser.uid));
        const clientsSnapshot = await getDocs(clientsQuery);
        const clientsData = clientsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClients(clientsData.sort((a, b) => {
          const nameA = a.name || `${a.firstName} ${a.lastName}`;
          const nameB = b.name || `${b.firstName} ${b.lastName}`;
          return nameA.localeCompare(nameB);
        }));

        // Fetch all pets
        const petsRef = collection(db, 'pets');
        const petsQuery = query(petsRef, where('sitterId', '==', currentUser.uid));
        const petsSnapshot = await getDocs(petsQuery);
        const petsData = petsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPets(petsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [currentUser, isOpen]);

  // Reset form for new appointments with selected date
  useEffect(() => {
    if (isOpen && !editingAppointment) {
      const dateToUse = selectedDate || new Date();
      const dateStr = format(dateToUse, 'yyyy-MM-dd');
      setFormData({
        clientId: '',
        petId: '',
        startDate: dateStr,
        endDate: dateStr,
        time: '09:00',
        serviceType: 'walking',
        location: '',
        duration: '30',
        notes: '',
        amount: 25,
        paymentStatus: 'pending',
      });
    }
  }, [isOpen, editingAppointment, selectedDate]);

  // Load editing appointment data
  useEffect(() => {
    if (editingAppointment) {
      setFormData({
        clientId: editingAppointment.clientId || '',
        petId: editingAppointment.petId || '',
        startDate: editingAppointment.date || editingAppointment.startDate,
        endDate: editingAppointment.endDate || editingAppointment.date || editingAppointment.startDate,
        time: editingAppointment.time || '09:00',
        serviceType: editingAppointment.serviceType || 'walking',
        location: editingAppointment.location || '',
        duration: editingAppointment.duration || '30',
        notes: editingAppointment.notes || '',
        amount: editingAppointment.amount || 25,
        paymentStatus: editingAppointment.paymentStatus || 'pending',
      });
    }
  }, [editingAppointment]);

  // Update available pets when client changes
  useEffect(() => {
    if (formData.clientId) {
      const clientPets = pets.filter(pet => pet.clientId === formData.clientId);
      setSelectedClientPets(clientPets);
      
      // Auto-fill location from client address
      const selectedClient = clients.find(c => c.id === formData.clientId);
      if (selectedClient && selectedClient.address && !formData.location) {
        setFormData(prev => ({ ...prev, location: selectedClient.address }));
      }
      
      // Reset pet selection if current pet doesn't belong to selected client
      if (formData.petId && !clientPets.find(p => p.id === formData.petId)) {
        setFormData(prev => ({ ...prev, petId: '' }));
      }
    } else {
      setSelectedClientPets([]);
      setFormData(prev => ({ ...prev, petId: '' }));
    }
  }, [formData.clientId, pets, clients]);

  // Update price when service type changes
  useEffect(() => {
    if (!editingAppointment) {
      setFormData(prev => ({ ...prev, amount: servicePresets[prev.serviceType] || 25 }));
    }
  }, [formData.serviceType, editingAppointment]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  // Quick Add Client
  const handleQuickAddClient = async () => {
    if (!quickClientData.firstName.trim() || !quickClientData.lastName.trim()) {
      alert('Please enter both first and last name');
      return;
    }

    try {
      const clientsRef = collection(db, 'clients');
      const docRef = await addDoc(clientsRef, {
        firstName: quickClientData.firstName.trim(),
        lastName: quickClientData.lastName.trim(),
        name: `${quickClientData.firstName.trim()} ${quickClientData.lastName.trim()}`,
        email: quickClientData.email.trim(),
        phone: quickClientData.phone.trim(),
        address: '',
        sitterId: currentUser.uid,
        createdAt: new Date().toISOString(),
      });

      const newClient = {
        id: docRef.id,
        firstName: quickClientData.firstName.trim(),
        lastName: quickClientData.lastName.trim(),
        name: `${quickClientData.firstName.trim()} ${quickClientData.lastName.trim()}`,
        email: quickClientData.email.trim(),
        phone: quickClientData.phone.trim(),
      };

      setClients([...clients, newClient].sort((a, b) => {
        const nameA = a.name || `${a.firstName} ${a.lastName}`;
        const nameB = b.name || `${b.firstName} ${b.lastName}`;
        return nameA.localeCompare(nameB);
      }));
      setFormData({ ...formData, clientId: docRef.id });
      setShowQuickAddClient(false);
      setQuickClientData({ firstName: '', lastName: '', email: '', phone: '' });
      alert('Client added successfully!');
    } catch (error) {
      console.error('Error adding client:', error);
      alert('Failed to add client. Please try again.');
    }
  };

  // Quick Add Pet
  const handleQuickAddPet = async () => {
    if (!formData.clientId) {
      alert('Please select a client first');
      return;
    }

    if (!quickPetData.name.trim()) {
      alert('Please enter pet name');
      return;
    }

    try {
      const petsRef = collection(db, 'pets');
      const docRef = await addDoc(petsRef, {
        name: quickPetData.name.trim(),
        type: quickPetData.type,
        breed: quickPetData.breed.trim(),
        clientId: formData.clientId,
        sitterId: currentUser.uid,
        createdAt: new Date().toISOString(),
      });

      const newPet = {
        id: docRef.id,
        name: quickPetData.name.trim(),
        type: quickPetData.type,
        breed: quickPetData.breed.trim(),
        clientId: formData.clientId,
      };

      setPets([...pets, newPet]);
      setSelectedClientPets([...selectedClientPets, newPet]);
      setFormData({ ...formData, petId: docRef.id });
      setShowQuickAddPet(false);
      setQuickPetData({ name: '', type: 'dog', breed: '' });
      alert('Pet added successfully!');
    } catch (error) {
      console.error('Error adding pet:', error);
      alert('Failed to add pet. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const client = clients.find(c => c.id === formData.clientId);
      const pet = pets.find(p => p.id === formData.petId);

      const appointmentData = {
        userId: currentUser.uid,
        clientId: formData.clientId,
        clientName: client?.name || `${client?.firstName} ${client?.lastName}`,
        petId: formData.petId,
        petName: pet?.name,
        date: formData.startDate, // Keep for compatibility
        startDate: formData.startDate,
        endDate: formData.endDate,
        time: formData.time,
        serviceType: formData.serviceType,
        location: formData.location,
        duration: formData.duration,
        notes: formData.notes,
        amount: parseFloat(formData.amount) || 0,
        paymentStatus: formData.paymentStatus,
      };

      if (editingAppointment) {
        // Update existing appointment
        const appointmentRef = doc(db, 'appointments', editingAppointment.id);
        await updateDoc(appointmentRef, {
          ...appointmentData,
          updatedAt: new Date().toISOString(),
        });
      } else {
        // Create new appointment
        await addDoc(collection(db, 'appointments'), {
          ...appointmentData,
          createdAt: new Date().toISOString(),
        });
      }

      alert(editingAppointment ? '✅ Appointment updated successfully!' : '✅ Appointment created successfully!');
      onClose(); // Close modal and let parent component refresh
    } catch (error) {
      console.error('Error saving appointment:', error);
      alert('❌ Failed to save appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto overflow-x-hidden">
      <div className="bg-white rounded-none sm:rounded-2xl max-w-3xl w-full min-h-screen sm:min-h-0 sm:my-8 max-h-screen sm:max-h-[90vh] overflow-y-auto overflow-x-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white p-4 sm:p-6 border-b border-brand-brown/10 rounded-t-none sm:rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-bold text-brand-brown truncate mr-4">
              {editingAppointment ? 'Edit Appointment' : 'New Appointment'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            >
              <X className="h-5 w-5 text-brand-brown" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-5 overflow-x-hidden">
          {/* Client Selection with Quick Add */}
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-2">
              Client *
            </label>
            <div className="flex space-x-2">
              <select
                required
                value={formData.clientId}
                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                className="input flex-1"
              >
                <option value="">Select a client...</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name || `${client.firstName} ${client.lastName}`}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowQuickAddClient(true)}
                className="px-4 py-2 bg-pastel-green text-brand-brown rounded-xl hover:bg-pastel-green/80 flex items-center space-x-2 font-medium shadow-sm"
                title="Quick add new client"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">New Client</span>
              </button>
            </div>
          </div>

          {/* Quick Add Client Form */}
          {showQuickAddClient && (
            <div className="p-4 bg-pastel-green/10 rounded-xl border-2 border-pastel-green space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-brand-brown">Quick Add Client</h4>
                <button
                  type="button"
                  onClick={() => {
                    setShowQuickAddClient(false);
                    setQuickClientData({ firstName: '', lastName: '', email: '', phone: '' });
                  }}
                  className="text-brand-brown/60 hover:text-brand-brown"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="First Name *"
                  value={quickClientData.firstName}
                  onChange={(e) => setQuickClientData({ ...quickClientData, firstName: e.target.value })}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Last Name *"
                  value={quickClientData.lastName}
                  onChange={(e) => setQuickClientData({ ...quickClientData, lastName: e.target.value })}
                  className="input text-sm"
                />
              </div>
              <input
                type="email"
                placeholder="Email"
                value={quickClientData.email}
                onChange={(e) => setQuickClientData({ ...quickClientData, email: e.target.value })}
                className="input text-sm"
              />
              <input
                type="tel"
                placeholder="Phone"
                value={quickClientData.phone}
                onChange={(e) => setQuickClientData({ ...quickClientData, phone: e.target.value })}
                className="input text-sm"
              />
              <button
                type="button"
                onClick={handleQuickAddClient}
                className="w-full btn-primary py-2 text-sm"
              >
                Add Client
              </button>
            </div>
          )}

          {/* Pet Selection with Quick Add */}
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-2">
              Pet *
            </label>
            <div className="flex space-x-2">
              <select
                required
                value={formData.petId}
                onChange={(e) => setFormData({ ...formData, petId: e.target.value })}
                className="input flex-1"
                disabled={!formData.clientId}
              >
                <option value="">
                  {formData.clientId ? 'Select a pet...' : 'Select a client first'}
                </option>
                {selectedClientPets.map(pet => (
                  <option key={pet.id} value={pet.id}>
                    {pet.name} ({pet.type})
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowQuickAddPet(true)}
                className="px-4 py-2 bg-pastel-pink text-brand-brown rounded-xl hover:bg-pastel-pink/80 flex items-center space-x-2 font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title="Quick add new pet"
                disabled={!formData.clientId}
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">New Pet</span>
              </button>
            </div>
          </div>

          {/* Quick Add Pet Form */}
          {showQuickAddPet && (
            <div className="p-4 bg-pastel-pink/10 rounded-xl border-2 border-pastel-pink space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-brand-brown">Quick Add Pet</h4>
                <button
                  type="button"
                  onClick={() => {
                    setShowQuickAddPet(false);
                    setQuickPetData({ name: '', type: 'dog', breed: '' });
                  }}
                  className="text-brand-brown/60 hover:text-brand-brown"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <input
                type="text"
                placeholder="Pet Name *"
                value={quickPetData.name}
                onChange={(e) => setQuickPetData({ ...quickPetData, name: e.target.value })}
                className="input text-sm"
              />
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={quickPetData.type}
                  onChange={(e) => setQuickPetData({ ...quickPetData, type: e.target.value })}
                  className="input text-sm"
                >
                  <option value="dog">Dog</option>
                  <option value="cat">Cat</option>
                  <option value="bird">Bird</option>
                  <option value="rabbit">Rabbit</option>
                  <option value="other">Other</option>
                </select>
                <input
                  type="text"
                  placeholder="Breed"
                  value={quickPetData.breed}
                  onChange={(e) => setQuickPetData({ ...quickPetData, breed: e.target.value })}
                  className="input text-sm"
                />
              </div>
              <button
                type="button"
                onClick={handleQuickAddPet}
                className="w-full btn-primary py-2 text-sm"
              >
                Add Pet
              </button>
            </div>
          )}

          {/* Start and End Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-brand-brown mb-2 flex items-center space-x-2">
                <CalendarIcon className="h-4 w-4" />
                <span>Start Date *</span>
              </label>
              <input
                type="date"
                required
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-brown mb-2 flex items-center space-x-2">
                <CalendarIcon className="h-4 w-4" />
                <span>End Date *</span>
              </label>
              <input
                type="date"
                required
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                min={formData.startDate}
                className="input"
              />
            </div>
          </div>

          {/* Time and Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-brand-brown mb-2 flex items-center space-x-2">
                <Clock className="h-4 w-4" />
                <span>Time *</span>
              </label>
              <input
                type="time"
                required
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-brown mb-2">
                Duration (minutes)
              </label>
              <select
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="input"
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">1 hour</option>
                <option value="120">2 hours</option>
                <option value="240">4 hours</option>
                <option value="480">Full day</option>
              </select>
            </div>
          </div>

          {/* Service Type */}
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-2">
              Service Type *
            </label>
            <select
              required
              value={formData.serviceType}
              onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
              className="input"
            >
              <option value="walking">Dog Walking</option>
              <option value="sitting">Pet Sitting</option>
              <option value="daycare">Doggy Day Care</option>
              <option value="checkin">Check In</option>
            </select>
          </div>

          {/* Pricing */}
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-2 flex items-center space-x-2">
              <DollarSign className="h-4 w-4" />
              <span>Price</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {Object.entries(servicePresets).map(([service, price]) => (
                <button
                  key={service}
                  type="button"
                  onClick={() => setFormData({ ...formData, amount: price })}
                  className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                    formData.amount === price
                      ? 'bg-pastel-blue text-brand-brown'
                      : 'bg-pastel-blue/20 text-brand-brown/70 hover:bg-pastel-blue/40'
                  }`}
                >
                  ${price}
                </button>
              ))}
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-3xl font-bold text-brand-brown">$</span>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                min="0"
                step="0.01"
                className="input flex-1 text-lg font-semibold"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-2 flex items-center space-x-2">
              <MapPin className="h-4 w-4" />
              <span>Location</span>
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Client's address or meeting location"
              className="input"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-2 flex items-center space-x-2">
              <FileText className="h-4 w-4" />
              <span>Notes</span>
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any special instructions or notes..."
              rows="3"
              className="textarea"
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex space-x-3 pt-4 border-t border-brand-brown/10">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary py-3"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn-primary py-3 flex items-center justify-center space-x-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CalendarIcon className="h-5 w-5" />
                  <span>{editingAppointment ? 'Update' : 'Create'} Appointment</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentModal;
